/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useLoader from "../../../hooks/useLoader";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import Timeline from "../../../components/Timeline";
import {
  faEdit,
  faTrash,
  faUndo,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import PageHeader from "../../../components/PageHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAction from "../../../hooks/useAction";
import { usePermissions } from "../../../hooks/usePermissions";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const HistoryEventsContractPage = () => {
  const navigate = useNavigate();
  const { canAccess, userRoles = [] } = usePermissions();
  const canViewContractEvents =
    userRoles.some(({ name }) =>
      ["Admin", "Admin Sênior", "Gestor", "Gestor de Contratos"].includes(name),
    ) || canAccess("Ver eventos de contratos");
  const { contractId } = useParams();

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  // --- Padronização: Gerenciamento de Modal de Confirmação e Ações via useAction ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction: confirmModalAction,
    handleCancelConfirmation,
    selectedItem: selectedEvent,
  } = useAction(navigate);

  const mapEventTypes = useCallback((eventTypesData) => {
    return Object.fromEntries(
      eventTypesData.map((eventType) => [
        eventType.id,
        { name: eventType.name, description: eventType.description },
      ]),
    );
  }, []);

  //  Função para formatar o diff de cada aditivo em HTML legível
  const buildAdditiveDetails = useCallback(
    (add, eventTypesMap) => {
      const typeName = eventTypesMap[add.type_id]?.name || "Não informado";
      const fmtDate = (d) =>
        d
          ? new Date(d).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "-";
      const prev = fmtDate(add.previous_date);
      const eff = fmtDate(add.effective_date);
      const fmtMoney = (v) =>
        Number(v).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const lines = [];
      const rawChanges = add?.changes;
      let changes = [];

      if (Array.isArray(rawChanges)) {
        changes = rawChanges;
      } else if (rawChanges && typeof rawChanges === "object") {
        if (rawChanges.new_end_date !== undefined) {
          changes = [rawChanges];
        } else {
          const infoChanges = Object.entries(rawChanges.info || {}).map(
            ([field, values]) => ({ kind: "info", field, ...values }),
          );
          const itemChanges = Array.isArray(rawChanges.items)
            ? rawChanges.items
            : [];
          const jobChanges = Array.isArray(rawChanges.jobs)
            ? rawChanges.jobs
            : [];
          changes = [...infoChanges, ...itemChanges, ...jobChanges];
        }
      }

      changes.forEach((c) => {
        if (!c || typeof c !== "object") return;

        if (c.kind === "info") {
          const infoLabels = {
            start_date: "Início da vigência",
            end_date: "Fim da vigência",
            max_end_date: "Fim máximo da vigência",
            duration: "Duração",
            max_duration: "Duração máxima",
          };
          const original = c.field?.includes("date")
            ? fmtDate(c.original)
            : c.original ?? "-";
          const updated = c.field?.includes("date")
            ? fmtDate(c.new)
            : c.new ?? "-";
          lines.push(
            `• ${infoLabels[c.field] || c.field}: ${original} → <strong>${updated}</strong>`,
          );
        } else if (c.quantity_added !== undefined) {
          const novaQtd = c.new
            ? c.quantity_added
            : c.original_quantity + c.quantity_added;
          lines.push(`• <strong>${c.alias}</strong> — ${c.description}`);
          if (c.new) {
            lines.push(
              `&nbsp;&nbsp;🆕 Item novo: <strong>${novaQtd} un</strong> (incluído em ${eff})`,
            );
          } else {
            lines.push(
              `&nbsp;&nbsp;📅 Antes: <strong>${c.original_quantity} un</strong> (vigorou de ${prev} até ${eff})`,
            );
            lines.push(
              `&nbsp;&nbsp;📈 Agora: <strong>${novaQtd} un</strong> (alterado em ${eff}, +${c.quantity_added})`,
            );
          }
        } else if (c.quantity_suppressed !== undefined) {
          lines.push(
            `• <strong>${c.alias}</strong>: ${c.original_quantity} → ${c.current_quantity} un (suprimido em ${eff})`,
          );
        } else if (c.new_price !== undefined) {
          lines.push(
            `• <strong>${c.alias}</strong>: R$ ${fmtMoney(c.original_price)} → R$ ${fmtMoney(c.new_price)} (${c.percent_difference > 0 ? "+" : ""}${c.percent_difference}% — ${eff})`,
          );
        } else if (c.new_end_date !== undefined) {
          lines.push(
            `• Prazo: ${fmtDate(c.original_end_date)} → <strong>${fmtDate(c.new_end_date)}</strong> (+${c.days_added} dias)`,
          );
        } else if (c.new_alias !== undefined) {
          lines.push(
            `• Item/serviço modificado: ${c.original_alias} → <strong>${c.new_alias}</strong> (em ${eff})`,
          );
        }
      });

      if (lines.length === 0) {
        return `${typeName}: ${eventTypesMap[add.type_id]?.description || "Nenhuma alteração registrada."}`;
      }

      return `<strong>${typeName}</strong>:<br/>${lines.join("<br/>")}`;
    },
    [],
  );

  const transformContractEvents = useCallback(
  (contractEvents, eventTypesMap, additivesData, historyMap) => {
    if (!additivesData || !contractEvents) return [];
    return contractEvents?.map((event) => {
      const history = historyMap?.[event.id];
      let additivesDetails;

      if (history?.additives?.length) {
        //  Usa os diffs detalhados do novo endpoint
        additivesDetails = history.additives
          .map((add) => buildAdditiveDetails(add, eventTypesMap))
          .join("<br/><br/>");
      } else {
        // Fallback (quando o histórico detalhado não existe)
        const currentAdditives = additivesData[event.id] || [];
        additivesDetails = currentAdditives
          .map((additive) => {
            const eventTypeName =
              eventTypesMap[additive?.contract_event_type_id]?.name ||
              "Não informado";
            const eventTypeDescription =
              eventTypesMap[additive?.contract_event_type_id]?.description ||
              "Não informado";
            return `${eventTypeName}: ${eventTypeDescription}`;
          })
          .join("<br />");
      }

      return {
        id: event.id,
        title: "#" + event.id,
        description: `${
          additivesDetails ? `Aditivos: <br />${additivesDetails}` : "Nenhum aditivo."
        }`,
        date: history?.date ?? event.created_at,
        status: event.deleted_at ? "inativo" : "ativo",
        isDeleted: !!event.deleted_at,
      };
    });
  },
  [buildAdditiveDetails],
);

  // --- Queries com TanStack Query ---
  const { data: eventTypesMap, isLoading: isLoadingEventTypes } =
    useQueryWithLoader({
      queryKey: ["eventTypes"],
      queryFn: async () => {
        const filters = transformFiltersForUrl({ deleted_at: false });
        const params = { filters };
        const response = await baseServiceGet(
          entities.contractEventTypes.get,
          params,
        );
        return response.result.data;
      },
      refetchOnWindowFocus: true,
      select: mapEventTypes,
    });

  const { data: additivesData, isLoading: isLoadingAdditives } =
    useQueryWithLoader({
      queryKey: ["eventAdditives"],
      queryFn: async () => {
        const filters = transformFiltersForUrl({
          deleted_at: false,
          "event.contract_id": contractId,
        });
        const params = { filters };
        const response = await baseServiceGet(entities.additives.get, params);
        return response.result;
      },
      refetchOnWindowFocus: true,
      select: (data) => {
        return data.reduce((acc, additive) => {
          if (!acc[additive.event_id]) {
            acc[additive.event_id] = [];
          }
          acc[additive.event_id].push(additive);
          return acc;
        }, {});
      },
    });

  const { data: contractEventsData, isLoading: isLoadingContractEvents } =
    useQueryWithLoader({
      queryKey: ["contractEvents", contractId],
      queryFn: async () => {
        const filters = transformFiltersForUrl({ deleted_at: false });
        const params = { filters };
        const response = await baseServiceGet(
          entities.contracts.events(contractId),
          params,
        );
        return response.result.data;
      },
      enabled: !!eventTypesMap && !!additivesData,
      refetchOnWindowFocus: true,
    });

  //  Query para buscar o histórico detalhado (com diffs)
  const { data: historyByEvent, isLoading: isLoadingHistory } =
    useQueryWithLoader({
      queryKey: ["contractEventsHistory", contractId],
      queryFn: async () => {
        const response = await baseServiceGet(
          entities.contracts.contractEventsHistory(contractId),
        );
        return response.result;
      },
      enabled: !!contractId,
      refetchOnWindowFocus: true,
      select: (data) =>
        Object.fromEntries(data.map((e) => [e.event_id, e])),
    });

  // --- Função para verificar se é permitido editar o evento ---
  const isLastEvent = useCallback(
    (event) => {
      if (!contractEventsData?.length) return false;

      const lastEvent = contractEventsData.reduce((prev, current) =>
        current.id > prev.id ? current : prev,
      );

      return lastEvent?.id === event?.id;
    },
    [contractEventsData],
  );

  // Não permitir editar o evento de cadastro do contrato, quando existir mais de um aditivo
  const canEditEvent = useCallback(
    (event) => {
      if (!additivesData || !contractEventsData) return false;

      const eventIds = contractEventsData.map((e) => e.id);
      const additivesOfThisEvent = additivesData[event?.id] || [];

      const isCadastroEvent = additivesOfThisEvent.some(
        (additive) => additive.contract_event_type_id === 1,
      );

      const totalAdditives = Object.values(additivesData)
        .flat()
        .filter((additive) => eventIds.includes(additive.event_id)).length;

      // Só permite editar se for o último evento
      if (!isLastEvent(event)) {
        return false;
      }

      // Não permitir editar cadastro se houver mais de 1 aditivo
      if (isCadastroEvent && totalAdditives > 1) {
        return false;
      }

      // Se tiver requisições vinculadas ao contrato
      if (
        contractEventsData.some((e) => e.contract?.has_associated_requests) &&
        isCadastroEvent
      ) {
        return false;
      }

      return true;
    },
    [additivesData, contractEventsData, isLastEvent],
  );

  const contractEvents = transformContractEvents(
    contractEventsData,
    eventTypesMap,
    additivesData,
    historyByEvent,
  );

  const canDeleteEvent = useCallback(
    (event) => {
      if (!additivesData || !contractEventsData) return false;

      const additivesDetails = additivesData[event?.id] || [];

      const hasContratoType = additivesDetails.some(
        (additive) => additive.contract_event_type_id === 1,
      );

      // Só permite deletar se for o último evento
      if (!isLastEvent(event)) {
        return false;
      }

      // Se o último evento for cadastro, não pode deletar
      if (hasContratoType) {
        return false;
      }

      return true;
    },
    [additivesData, contractEventsData, isLastEvent],
  );

  // --- Funções de mutação para DELETE e PUT (ativar) ---
  // Mutação para DELETAR evento
  const deleteEventMutation = useMutation({
    mutationFn: async (eventIdToDelete) => {
      const response = await baseServiceDelete(
        entities.contracts.contractEventDelete(contractId, eventIdToDelete),
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["contractEvents", contractId]);
    },
    onError: (error) => {
      console.error("onError", error);
      hideLoader();
    },
  });

  // Mutação para ATIVAR evento (exemplo, você precisaria de um endpoint para isso)
  const activateEventMutation = deleteEventMutation;

  // --- Gerenciamento do Loader Global (incluindo estados de mutação) ---
  useEffect(() => {
    const isAnyLoadingOrPending =
      isLoadingEventTypes ||
      isLoadingAdditives ||
      isLoadingContractEvents ||
      isLoadingHistory ||
      deleteEventMutation.isPending ||
      activateEventMutation.isPending;
    if (isAnyLoadingOrPending) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [
    isLoadingEventTypes,
    isLoadingAdditives,
    isLoadingContractEvents,
    isLoadingHistory,
    deleteEventMutation.isPending,
    activateEventMutation.isPending,
    showLoader,
    hideLoader,
  ]);

  // --- Ações para o Timeline (useMemo, dependendo das funções de handler) ---
  const actions = useMemo(
    () => [
      {
        id: "view",
        icon: faEye,
        title: "Ver Detalhes",
        buttonClass: "btn-secondary",
        permission: "Ver eventos de contratos",
        onClick: (event) =>
          navigate(
            `/aluguel/contratos/${contractId}/eventos/${event.id}/detalhes`,
          ),
        show: (event) => !event.isDeleted && canViewContractEvents,
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar eventos de contratos",
        onClick: (event) =>
          navigate(
            `/aluguel/contratos/${contractId}/eventos/${event.id}/editar`,
          ),
        show: (event) =>
          !event.isDeleted &&
          canEditEvent(event) &&
          canAccess("Atualizar eventos de contratos"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir eventos de contratos",
        onClick: (event) =>
          showConfirmation(
            event,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await deleteEventMutation.mutateAsync(event.id);
              } catch (error) {
                console.error("Erro ao excluir evento:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (event) =>
          !event.isDeleted &&
          canDeleteEvent(event) &&
          canAccess("Excluir eventos de contratos"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir eventos de contratos",
        onClick: (event) =>
          showConfirmation(
            event,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateEventMutation.mutateAsync(event.id);
              } catch (error) {
                console.error("Erro ao restaurar evento:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (event) =>
          event.isDeleted &&
          canDeleteEvent(event) &&
          canAccess("Excluir eventos de contratos"),
      },
    ],
    [
      activateEventMutation,
      canAccess,
      canViewContractEvents,
      canEditEvent,
      contractId,
      deleteEventMutation,
      navigate,
      canDeleteEvent,
      showConfirmation,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Histórico de Eventos do Contrato"
        showBackButton={true}
        backUrl={`/aluguel/contratos`}
      />
      <div className="container-fluid p-1">
        <Timeline data={contractEvents || []} actions={actions} />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={confirmModalAction}
          itemName={selectedEvent ? `${selectedEvent.title}` : ""}
          text={action.text}
          isLoading={
            deleteEventMutation.isPending || activateEventMutation.isPending
          }
        />
      </div>
    </MainLayout>
  );
};

export default HistoryEventsContractPage;
